import React, {
  Context,
  useMemo,
  createContext,
  ReactNode,
  SetStateAction,
  useCallback,
} from "react";
import {
  parseAsArrayOf,
  parseAsString,
  parseAsInteger,
  useQueryStates,
} from "nuqs";

import { useNosto } from "~/hooks/useNosto";

import { type ProductCard } from "types/product";

type NostoProviderProps = {
  children: ReactNode;
  collectionTitle: string;
  gender: "men" | "women" | null;
};

export type SelectedFilter = {
  field: string;
  value: string[];
  name: string;
};

export type SortOption = {
  field: string;
  order: string;
  label: string;
};

export type NostoContextProps = {
  products: (ProductCard & {
    reviewGroup: string | null;
  })[];
  facets: {
    id: string;
    name: string;
    field: string;
    type: string;
    data: { value: string; count: number; selected: boolean }[];
  }[];
  productCount: number;
  selectedFilters: SelectedFilter[];
  sortOptions: SortOption[];
  sortOption: SortOption;
  setSortOption: (sortOption: SortOption) => void;
  setSelectedFilters: (
    update: SetStateAction<SelectedFilter[]>,
    resetPage?: boolean,
  ) => void;
  handleFilterChange: (field: string, name: string, value: string) => void;
  handleFilterRemove: (field: string, value: string) => void;
  gender: "men" | "women";
  showViewToggle: boolean;
  page: number;
  setPage: (page: number) => void;
};

const sortOptions: SortOption[] = [
  {
    field: "relevance",
    order: "desc",
    label: "Best Seller",
  },
  {
    field: "datePublished",
    order: "desc",
    label: "New Arrivals",
  },
  {
    field: "listPrice",
    order: "asc",
    label: "Lowest Price",
  },
  {
    field: "listPrice",
    order: "desc",
    label: "Highest Price",
  },
];

export const NostoContext: Context<NostoContextProps> =
  createContext<NostoContextProps>({
    products: [],
    facets: [],
    productCount: 0,
    selectedFilters: [],
    sortOptions: sortOptions,
    sortOption: sortOptions[0],
    setSortOption: () => {},
    setSelectedFilters: () => {},
    handleFilterChange: () => {},
    handleFilterRemove: () => {},
    gender: "women",
    showViewToggle: true,
    page: 0,
    setPage: () => {},
  });

const shouldShowViewToggle = (collectionTitle: string) => {
  const excludedCollections = ["Shoe Care", "Pet", "Socks & Accessories"];
  return !excludedCollections.some((name) => collectionTitle?.includes(name));
};

const toggleArrayValue = <T,>(array: T[], value: T): T[] =>
  array.includes(value) ? array.filter((v) => v !== value) : [...array, value];

// Helper function to update filter list by toggling a value
const toggleFilterValue = (
  filters: SelectedFilter[],
  field: string,
  name: string,
  value: string,
): SelectedFilter[] => {
  const currentFilter = filters.find((f) => f.field === field);
  const currentValues = currentFilter?.value ?? [];
  const newValues = toggleArrayValue(currentValues, value);
  const otherFilters = filters.filter((f) => f.field !== field);

  return newValues.length > 0
    ? [...otherFilters, { field, name, value: newValues }]
    : otherFilters;
};

export const NostoProvider = ({
  children,
  collectionTitle,
  gender,
}: NostoProviderProps) => {
  /*
   ** Map each known facet to a stable query key. These become URL params:
   ** ?filter.skus.customFields.size=34&filter.customFields.custom-netsuite_sub_category=Casual+Heels
   */
  const [queryParams, setQueryParams] = useQueryStates(
    {
      "filter.customFields.custom-netsuite_category": parseAsArrayOf(
        parseAsString,
      ).withDefault([]),
      "filter.customFields.custom-netsuite_sub_category": parseAsArrayOf(
        parseAsString,
      ).withDefault([]),
      "filter.skus.customFields.size": parseAsArrayOf(
        parseAsString,
      ).withDefault([]),
      "filter.customFields.custom-netsuite_heel_height": parseAsArrayOf(
        parseAsString,
      ).withDefault([]),
      "filter.ai.primaryColor": parseAsArrayOf(parseAsString).withDefault([]),
      sort: parseAsString.withDefault(sortOptions[0].label.toLowerCase()),
      page: parseAsInteger.withDefault(1),
    },
    { history: "replace" },
  );

  const { sort: sortParam, page: pageParam, ...filters } = queryParams;
  const page = pageParam;

  // Determine current sort option from URL (with default from useQueryStates)
  const sortOption = useMemo(() => {
    const found = sortOptions.find((option) =>
      sortParam.includes(option.label.toLowerCase()),
    );
    return found ?? sortOptions[0];
  }, [sortParam]);

  const selectedFilters = useMemo(
    () =>
      Object.entries(filters)
        .filter(([_, value]) => value.length > 0)
        .map(([key, value]) => ({
          field: key.replace("filter.", ""),
          value,
        })),
    [filters],
  );

  const setSelectedFilters = useCallback(
    (update: SetStateAction<SelectedFilter[]>, resetPage = false) => {
      const newFilters =
        typeof update === "function"
          ? update(selectedFilters as SelectedFilter[])
          : update;

      // Convert SelectedFilter[] back to URL params format
      const filterParams = Object.fromEntries(
        Object.keys(filters).map((key) => {
          const filter = newFilters.find(
            (filter) => `filter.${filter.field}` === key,
          );
          return [key, filter?.value ?? null];
        }),
      );

      // Update filters and optionally reset page
      setQueryParams({
        ...Object.fromEntries(
          Object.entries(filterParams).filter(([_, v]) => v !== undefined),
        ),
        ...(resetPage ? { page: 1 } : {}),
      });
    },
    [selectedFilters, filters, setQueryParams],
  );

  const setPage = useCallback(
    (newPage: number) => setQueryParams({ page: newPage }),
    [setQueryParams],
  );

  const setSortOption = useCallback(
    (newSortOption: SortOption) => {
      const sortValue = newSortOption.label.toLowerCase();

      // Update sort and reset page together
      setQueryParams({ sort: sortValue, page: 1 });

      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [setQueryParams],
  );

  // Map selected filters to API filters format
  const mappedAPIFilters = useMemo(
    () => selectedFilters.map(({ field, value }) => ({ field, value })),
    [selectedFilters],
  );

  const { data } = useNosto({
    category: collectionTitle,
    filters: mappedAPIFilters,
    page,
    sort: [{ field: sortOption.field, order: sortOption.order }],
  });

  // Enrich filters with names from API response
  const enrichedFilters = useMemo(() => {
    if (!data?.facets) {
      return selectedFilters.map((filter) => ({ ...filter, name: "" }));
    }

    return selectedFilters.map((filter) => ({
      ...filter,
      name:
        data.facets.find((facet) => facet.field === filter.field)?.name ?? "",
    }));
  }, [selectedFilters, data?.facets]);

  const handleFilterChange = useCallback(
    (field: string, name: string, value: string) => {
      setSelectedFilters(
        (prevFilters) => toggleFilterValue(prevFilters, field, name, value),
        true,
      );

      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [setSelectedFilters],
  );

  const handleFilterRemove = useCallback(
    (field: string, valueToRemove: string) => {
      setSelectedFilters((prevFilters) => {
        const filter = prevFilters.find((filter) => filter.field === field);

        if (!filter) return prevFilters;

        const newValues = filter.value.filter((v) => v !== valueToRemove);
        const others = prevFilters.filter((filter) => filter.field !== field);

        return newValues.length > 0
          ? [...others, { ...filter, value: newValues }]
          : others;
      }, true);
    },
    [setSelectedFilters],
  );

  const contextValue = useMemo(
    () => ({
      products: data?.products ?? [],
      facets: data?.facets ?? [],
      productCount: data?.productCount ?? 0,
      selectedFilters: enrichedFilters,
      sortOptions,
      sortOption,
      setSortOption,
      page,
      setPage,
      setSelectedFilters,
      handleFilterChange,
      handleFilterRemove,
      gender: gender ?? "women",
      showViewToggle: shouldShowViewToggle(collectionTitle),
    }),
    [
      data?.products,
      data?.facets,
      data?.productCount,
      enrichedFilters,
      sortOptions,
      sortOption,
      setSortOption,
      page,
      setPage,
      setSelectedFilters,
      handleFilterChange,
      handleFilterRemove,
      gender,
      collectionTitle,
    ],
  );

  return (
    <NostoContext.Provider value={contextValue}>
      {children}
    </NostoContext.Provider>
  );
};
